import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiGift, FiUser, FiMail, FiPhone, FiCalendar, FiDollarSign, FiPackage, FiCheck, FiArrowLeft, FiSend, FiHeart } from 'react-icons/fi';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GiftCardRequestPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [products, setProducts] = useState([]);
  const [site, setSite] = useState(null);
  
  const [formData, setFormData] = useState({
    sender_name: '',
    sender_email: '',
    sender_phone: '',
    recipient_name: '',
    amount: '',
    prestation_type: '',
    event_date: '',
    message: ''
  });

  // Montants prédéfinis
  const predefinedAmounts = [50, 100, 150, 200, 250, 300, 400, 500];

  useEffect(() => {
    // Load products for prestation selection
    const loadProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/products`);
        if (res.ok) {
          const data = await res.json();
          // Filter out gift cards and inactive products
          const activeProducts = (data.products || data || []).filter(p => p.is_active !== false && !p.is_gift_card);
          setProducts(activeProducts);
        }
      } catch (err) {
        console.error('Error loading products:', err);
      }
    };
    
    // Load site data for styling
    const loadSite = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/site`);
        if (res.ok) {
          const data = await res.json();
          setSite(data);
        }
      } catch (err) {
        console.error('Error loading site:', err);
      }
    };
    
    loadProducts();
    loadSite();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sender_name || !formData.sender_email || !formData.recipient_name || !formData.amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/gift-card-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setSubmitted(true);
        toast.success('Votre demande a été envoyée !');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = site?.primary_color || '#d4af37';

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-black flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
            <FiCheck className="w-10 h-10" style={{ color: primaryColor }} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Demande envoyée !</h1>
          <p className="text-white/70 mb-8">
            Merci pour votre demande de carte cadeau. Notre équipe vous contactera dans les plus brefs délais pour finaliser votre achat.
            <br /><br />
            Une fois validée, la carte cadeau sera envoyée par email au format digital au destinataire.
          </p>
          <Link 
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-black hover:scale-105 transition-transform"
            style={{ backgroundColor: primaryColor }}
          >
            <FiArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-black">
      {/* Header */}
      <header className="py-6 px-4 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:opacity-80 transition-opacity">
            <FiArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </Link>
          {site?.logo_url && (
            <img src={site.logo_url} alt="Logo" className="h-10 object-contain" />
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
            <FiGift className="w-8 h-8" style={{ color: primaryColor }} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Offrez une Carte Cadeau
          </h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Faites plaisir à vos proches avec une expérience unique !
          </p>
        </motion.div>

        {/* Process Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          <div className="bg-white/5 rounded-2xl p-6 text-center border border-white/10">
            <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl" style={{ backgroundColor: `${primaryColor}20` }}>
              1
            </div>
            <h3 className="text-white font-semibold mb-2">Formulez votre demande</h3>
            <p className="text-white/60 text-sm">Remplissez le formulaire avec les informations du destinataire et le montant souhaité</p>
          </div>
          <div className="bg-white/5 rounded-2xl p-6 text-center border border-white/10">
            <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl" style={{ backgroundColor: `${primaryColor}20` }}>
              2
            </div>
            <h3 className="text-white font-semibold mb-2">Nous vous recontactons</h3>
            <p className="text-white/60 text-sm">Notre équipe vous contacte pour finaliser le paiement et personnaliser votre carte</p>
          </div>
          <div className="bg-white/5 rounded-2xl p-6 text-center border border-white/10">
            <div className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl" style={{ backgroundColor: `${primaryColor}20` }}>
              3
            </div>
            <h3 className="text-white font-semibold mb-2">Envoi par email</h3>
            <p className="text-white/60 text-sm">Le destinataire reçoit sa carte cadeau digitale par email, prête à être utilisée</p>
          </div>
        </motion.div>

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Sender Info */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FiUser className="w-5 h-5" style={{ color: primaryColor }} />
                Vos informations
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">Votre nom *</label>
                  <input
                    type="text"
                    value={formData.sender_name}
                    onChange={(e) => setFormData({ ...formData, sender_name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#d4af37]"
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">Votre email *</label>
                  <input
                    type="email"
                    value={formData.sender_email}
                    onChange={(e) => setFormData({ ...formData, sender_email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#d4af37]"
                    placeholder="jean@email.com"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-white/70 text-sm mb-2">Votre téléphone</label>
                  <input
                    type="tel"
                    value={formData.sender_phone}
                    onChange={(e) => setFormData({ ...formData, sender_phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#d4af37]"
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>
            </div>

            {/* Recipient Info */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FiHeart className="w-5 h-5" style={{ color: primaryColor }} />
                Pour qui est ce cadeau ?
              </h3>
              <div>
                <label className="block text-white/70 text-sm mb-2">Nom du destinataire *</label>
                <input
                  type="text"
                  value={formData.recipient_name}
                  onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#d4af37]"
                  placeholder="Marie Martin"
                  required
                />
              </div>
            </div>

            {/* Amount */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FiDollarSign className="w-5 h-5" style={{ color: primaryColor }} />
                Montant de la carte *
              </h3>
              <div className="grid grid-cols-4 gap-3 mb-4">
                {predefinedAmounts.map(amount => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setFormData({ ...formData, amount: amount.toString() })}
                    className={`py-3 rounded-xl font-semibold transition-all ${
                      formData.amount === amount.toString()
                        ? 'text-black scale-105'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                    style={formData.amount === amount.toString() ? { backgroundColor: primaryColor } : {}}
                  >
                    {amount}€
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Ou montant personnalisé</label>
                <div className="relative">
                  <input
                    type="number"
                    min="10"
                    value={!predefinedAmounts.includes(parseInt(formData.amount)) ? formData.amount : ''}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#d4af37]"
                    placeholder="Autre montant..."
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50">€</span>
                </div>
              </div>
            </div>

            {/* Prestation Type */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FiPackage className="w-5 h-5" style={{ color: primaryColor }} />
                Type de prestation souhaitée
              </h3>
              <select
                value={formData.prestation_type}
                onChange={(e) => setFormData({ ...formData, prestation_type: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#d4af37]"
              >
                <option value="" className="bg-slate-800">Toutes prestations (au choix)</option>
                {products.map(p => (
                  <option key={p.id} value={p.name} className="bg-slate-800">{p.name}</option>
                ))}
              </select>
            </div>

            {/* Event Date */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FiCalendar className="w-5 h-5" style={{ color: primaryColor }} />
                Date de l'événement <span className="text-white/50 text-sm font-normal">(optionnel)</span>
              </h3>
              <input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white focus:outline-none focus:border-[#d4af37]"
              />
            </div>

            {/* Message */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <FiMail className="w-5 h-5" style={{ color: primaryColor }} />
                Message personnalisé <span className="text-white/50 text-sm font-normal">(optionnel)</span>
              </h3>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#d4af37] resize-none"
                placeholder="Un petit mot pour accompagner votre cadeau..."
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl font-bold text-lg text-black flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <FiSend className="w-5 h-5" />
                  Envoyer ma demande
                </>
              )}
            </button>
          </form>
        </motion.div>

        {/* Footer Note */}
        <p className="text-center text-white/40 text-sm mt-8">
          La carte cadeau sera valable 1 an à compter de sa date d'émission.
          <br />
          Pour toute question, contactez-nous au {site?.contact_info?.phone || '06 12 34 56 78'}
        </p>
      </div>
    </div>
  );
};

export default GiftCardRequestPage;
